import React from 'react';
import logger from '../Services/logger';

/**
 * Error boundary para o code-splitting de rotas (React.lazy + Suspense).
 *
 * Erros de carregamento de chunk acontecem tipicamente quando o usuário está com
 * um index.html antigo em cache e pede um chunk que o deploy novo já removeu
 * (deploy é manual, sem cache-busting garantido — TD-007). Nesses casos, um
 * reload puxa o index/manifest atualizado e resolve. Guardamos um timestamp em
 * sessionStorage para NÃO entrar em loop de reload caso o erro persista.
 */

const CHUNK_ERROR_RE = /(ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/i;
const RELOAD_TS_KEY = '@dream4/chunkReloadedAt';
const RELOAD_COOLDOWN_MS = 10000; // só recarrega de novo se passou >10s do último reload

function isChunkLoadError(error) {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(error.message || '');
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0); } catch (e) { /* sessionStorage indisponível */ }

      // Só recarrega automaticamente se não recarregamos há pouco (evita loop)
      if (Date.now() - last > RELOAD_COOLDOWN_MS) {
        try { sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now())); } catch (e) { /* noop */ }
        window.location.reload();
        return;
      }
    }

    // Erro não-chunk (ou chunk que persiste após reload): reporta e mostra fallback.
    logger.error('[ChunkErrorBoundary] erro ao carregar/renderizar rota', error, info);
  }

  handleReload() {
    try { sessionStorage.removeItem(RELOAD_TS_KEY); } catch (e) { /* noop */ }
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', padding: '30px', textAlign: 'center', gap: '12px' }}>
          <div style={{ fontFamily: 'Poppins', fontSize: '1.25rem', fontWeight: 700, color: '#1D2939' }}>
            Não foi possível carregar esta página
          </div>
          <div style={{ fontFamily: 'Titillium Web', color: '#475467', maxWidth: '420px' }}>
            Pode ser uma atualização recente do sistema. Recarregue a página para continuar.
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            style={{ marginTop: '8px', padding: '10px 20px', borderRadius: '16px', border: 'none', backgroundColor: '#814DE5', color: '#FFF', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Recarregar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
